/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { EditOutlined } from '@ant-design/icons'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'

import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import type { GameGridRow } from '../utils/gameGridRows'

export interface GameEditCellContext {
  /** Opens the add/edit Game modal for this row. */
  onEditGame: (row: GameGridRow) => void
}

/**
 * Opens the full add/edit Game modal for one row.
 *
 * Sits beside the open-in-new-tab link, in the same pinned gutter of row
 * controls. The grid already edits most fields in place, but a few — teams,
 * "if necessary", livestream — have no cell of their own, and some admins would
 * rather change several fields at once behind one Confirm than commit each cell
 * as they go.
 *
 * Unlike the link next to it this IS gated on `canEdit`: the column is dropped
 * entirely for a read-only viewer rather than rendered disabled, since a button
 * that never does anything is worse than no button.
 */
const GameEditCell = ({
  data,
  context,
}: ICellRendererParams<GameGridRow, unknown, GameEditCellContext>) => {
  'use memo'

  //  AG Grid renders this cell for its loading and full-width rows with no data.
  if (data === undefined) {
    return null
  }

  //  A row with a PUT already in flight cannot start another edit — the same
  //  rule the in-place cell editors follow.
  const disabled = data.isPending

  //  Matches the view icon beside it: title-cased at the render site.
  const label = capitalizeWords('Edit Game')

  return (
    <Tooltip title={label} mouseEnterDelay={0.3}>
      <button
        type='button'
        onClick={() => context?.onEditGame?.(data)}
        disabled={disabled}
        css={buttonStyles}
        aria-label={label}
        data-testid={`game-edit-button-${data.id}`}
      >
        <EditOutlined aria-hidden />
      </button>
    </Tooltip>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const buttonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: ${colors.NEUTRAL_50};
  font-size: 15px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    color: ${colors.PRIMARY};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`

export default GameEditCell
